import { memo, useState } from 'react';
import cn from 'classnames';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Checkbox,
  RadioGroup,
  Radio,
} from '@heroui/react';
import {
  downloadDashboardExport,
  DASHBOARD_EXPORT_PATHS,
  type DashboardExportFormat,
  type DashboardExportParams,
} from '@/entities/Dashboard';
import { CustomDateRangePicker } from '@/shared/ui/Fields';
import { AppButton } from '@/shared/ui/AppButton';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useBodyOverflow } from '@/shared/lib/components/FloatingProvider';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { EXPORT_PAGE_OPTIONS } from '../../../config/dashboardTabs';
import styles from './ExportAnalyticsModal.module.scss';

const FILE_TYPE_OPTIONS = [
  { value: 'pdf' as const, title: 'PDF', description: 'Formatted report from analytics data.' },
  { value: 'excel' as const, title: 'Excel', description: 'Raw analytics data.' },
  { value: 'csv' as const, title: 'CSV', description: 'Plain data for spreadsheets.' },
];

const FILE_EXTENSIONS: Record<DashboardExportFormat, string> = {
  pdf: 'pdf',
  excel: 'xlsx',
  csv: 'csv',
};

const ALL_PAGES = [
  'general',
  'students',
  'courses',
  'assignments',
  'lecturers',
  'certificatesAchievements',
  'scorm',
] as const;

type ExportPage = (typeof ALL_PAGES)[number];

interface ExportAnalyticsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ExportAnalyticsModal = memo(({ isOpen, onOpenChange }: ExportAnalyticsModalProps) => {
  const { toast } = useToast();
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set(['all']));
  const [fileType, setFileType] = useState<DashboardExportFormat>('pdf');
  const [range, setRange] = useState<{ start?: string; end?: string }>({});
  const [isExporting, setIsExporting] = useState(false);

  useBodyOverflow({ elementId: 'export-analytics-modal', condition: isOpen, unlockOnUnmount: true });

  const togglePage = (key: string) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      if (key === 'all') {
        return new Set(['all']);
      }
      next.delete('all');
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next.size ? next : new Set(['all']);
    });
  };

  const buildParams = (page: ExportPage): DashboardExportParams => {
    const base: DashboardExportParams = {
      format: fileType,
      date: range.end,
    };
    if (page === 'general') {
      return { ...base, startDate: range.start, endDate: range.end };
    }
    if (page === 'courses') {
      return { ...base, sections: 'summary,top,performance' };
    }
    return base;
  };

  const handleExport = async (onClose: () => void) => {
    const pages: ExportPage[] = selectedPages.has('all')
      ? [...ALL_PAGES]
      : [...ALL_PAGES].filter((p) => selectedPages.has(p));

    if (!pages.length) return;

    setIsExporting(true);
    try {
      for (const page of pages) {
        await downloadDashboardExport(
          DASHBOARD_EXPORT_PATHS[page],
          buildParams(page),
          `${page}-analytics.${FILE_EXTENSIONS[fileType]}`,
        );
      }
      toast({ title: 'Success', description: 'Export completed', color: 'success' });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to export analytics',
        color: 'danger',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="center"
      size="lg"
      motionProps={scaleInOut}
      shouldBlockScroll={false}
      classNames={{ base: 'max-w-[520px]' }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="text-xl font-semibold">Export Analytics</ModalHeader>
            <ModalBody className={`flex flex-col gap-6 ${styles.modalBody}`}>
              <CustomDateRangePicker aria-label="Export period"
                selectorButtonPlacement="start"
                visibleMonths={2}
                onChange={(value) =>
                  setRange({
                    start: value?.start?.toString(),
                    end: value?.end?.toString(),
                  })
                }
              />

              <div className="flex flex-col gap-3">
                <p className="text-sm font-semibold text-black">Select pages</p>
                {EXPORT_PAGE_OPTIONS.map((option) => {
                  const isSelected = selectedPages.has(option.key);

                  return (
                    <Checkbox
                      key={option.key}
                      isSelected={isSelected}
                      onValueChange={() => togglePage(option.key)}
                      classNames={{
                        base: styles.checkboxControl,
                        label: cn(styles.controlLabel, isSelected && styles.controlLabelSelected),
                      }}
                    >
                      {option.label}
                    </Checkbox>
                  );
                })}
              </div>

              <div className="flex flex-col gap-3">
                <p className="text-sm font-semibold text-black">Select the file type for export</p>
                <RadioGroup
                  value={fileType}
                  onValueChange={(v) => setFileType(v as DashboardExportFormat)}
                  classNames={{ wrapper: styles.exportRadioGroup }}
                >
                  {FILE_TYPE_OPTIONS.map((option) => {
                    const isSelected = fileType === option.value;

                    return (
                      <Radio
                        key={option.value}
                        value={option.value}
                        classNames={{
                          base: styles.radioItem,
                          wrapper: styles.radioWrapper,
                          label: cn(styles.controlLabel, isSelected && styles.controlLabelSelected),
                        }}
                      >
                        <div className="flex flex-col">
                          <span
                            className={cn(
                              styles.exportOptionTitle,
                              isSelected && styles.exportOptionTitleSelected,
                            )}
                          >
                            {option.title}
                          </span>
                          <span className={styles.exportOptionDesc}>{option.description}</span>
                        </div>
                      </Radio>
                    );
                  })}
                </RadioGroup>
              </div>
            </ModalBody>
            <ModalFooter className={styles.modalFooter}>
              <AppButton color="white" className={styles.footerButton} onPress={onClose}>
                Cancel
              </AppButton>
              <AppButton
                color="black"
                className={styles.footerButton}
                isLoading={isExporting}
                onPress={() => handleExport(onClose)}
              >
                Export file
              </AppButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
});

ExportAnalyticsModal.displayName = 'ExportAnalyticsModal';
